/* Native Gardeners accessibility enhancements — added 2026-08-31, revised 2026-09-01, 09-02, 09-09
   Purpose (WCAG 2.1 AA alignment):
   1) Screen-reader announcements confirming cart additions/removals (WCAG 4.1.3 Status Messages)
   2) Current cart-item count exposed in the cart control's accessible name (WCAG 4.1.2 Name, Role, Value)
   3) Escape key closes the cart drawer (expected dialog behavior)
   4) Dialog semantics on the cart drawer as a runtime safety net
   5) Focus returns to the triggering control when the drawer closes (WCAG 2.4.3 Focus Order)
   6) Keyboard focus is kept inside the drawer while it is open, including across the theme's asynchronous cart re-render (WCAG 2.4.3)
   7) Tab/Shift+Tab move through every control in the drawer and wrap around; works around the theme's focus trap, which pins focus on the Close button when the first item is added to an empty cart (WCAG 2.1.1/2.1.2)
   Added 2026-09-09 (round 2):
   8) Header drop-down menus: the expanded/collapsed state is reported on the menu control itself and Escape closes an open menu (WCAG 4.1.2)
   9) Search suggestions: the number of suggestions is announced when they appear, and the search box reports whether its suggestion list is open (WCAG 4.1.3, 4.1.2)
   10) Pop-up dialogs (newsletter sign-up, quick shop, video): dialog role and accessible name, and focus goes back to where it was when the dialog closes (WCAG 4.1.2, 2.4.3)
   11) Pagination: navigation landmark, current page marked, page-number links named "Page N" (WCAG 1.3.1, 2.4.4)
   Version 3 — applied to the theme copy "ADA round 2 - preview then publish (Sept 9)" on 2026-09-09.
*/
(function () {
  'use strict';

  /* --- 1) Polite live region announcing cart changes --- */
  var live = document.getElementById('a11y-cart-status');
  if (!live) {
    live = document.createElement('div');
    live.id = 'a11y-cart-status';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    document.body.appendChild(live);
  }

  var lastCount = null;

  function itemsText(count) {
    return count + (count === 1 ? ' item' : ' items');
  }

  /* --- 2) Expose the current count on the cart control(s) --- */
  function updateCartControls(count) {
    var controls = document.querySelectorAll('a.js-drawer-open-cart, a[href="/cart"]');
    for (var i = 0; i < controls.length; i++) {
      var a = controls[i];
      var fallback = a.querySelector('.icon__fallback-text');
      var base = (fallback && fallback.textContent ? fallback.textContent : 'Cart').replace(/\s+/g, ' ').trim() || 'Cart';
      a.setAttribute('aria-label', base + ', ' + itemsText(count));
    }
  }

  function announce(count, totalCents) {
    if (count === lastCount) return;
    var msg;
    if (lastCount !== null && count > lastCount) msg = 'Item added to cart. ';
    else if (lastCount !== null && count < lastCount) msg = 'Item removed from cart. ';
    else msg = '';
    msg += 'Cart has ' + itemsText(count);
    if (typeof totalCents === 'number') msg += ', total $' + (totalCents / 100).toFixed(2);
    lastCount = count;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = msg + '.'; }, 50);
  }

  function refreshFromCart() {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          updateCartControls(cart.item_count);
          announce(cart.item_count, cart.total_price);
        })
        .catch(function () {});
    } catch (e) { /* no-op */ }
  }

  /* Initialize: label the control with the current count, silently */
  try {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCount = cart.item_count;
        updateCartControls(cart.item_count);
      })
      .catch(function () {});
  } catch (e) {}

  /* Motion updates the header cart bubble on every cart change — observe it */
  var bubble = document.querySelector('.cart-link__bubble');
  if (bubble && window.MutationObserver) {
    new MutationObserver(refreshFromCart)
      .observe(bubble, { childList: true, characterData: true, subtree: true, attributes: true });
  }
  /* Archetype themes also publish this event where available */
  document.addEventListener('cart:updated', refreshFromCart);

  /* --- 3 & 4) Cart drawer: dialog semantics + Escape closes --- */
  var drawer = document.getElementById('CartDrawer');
  if (drawer) {
    if (!drawer.getAttribute('role')) drawer.setAttribute('role', 'dialog');
    if (!drawer.getAttribute('aria-modal')) drawer.setAttribute('aria-modal', 'true');
    if (!drawer.getAttribute('aria-label')) drawer.setAttribute('aria-label', 'Shopping cart');
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!drawer.classList.contains('drawer--is-open')) return;
      var closeBtn = drawer.querySelector('.js-drawer-close');
      if (closeBtn) closeBtn.click();
    });

    /* --- 5) Return focus to the triggering control when the drawer closes (WCAG 2.4.3 Focus Order).
       The theme restores focus only when the drawer was opened from a cart icon; when it opens
       automatically after "Add to cart" (or is closed with Escape), focus would otherwise land on <body>.
       We remember the last focusable element used outside the drawer and restore it if focus is lost. --- */
    var lastOutside = null;
    function remember(el) {
      if (drawer.classList.contains('drawer--is-open')) return; /* focus that strays outside an open drawer is not the trigger */
      if (el && el !== document.body && el.nodeType === 1 && !drawer.contains(el)) lastOutside = el;
    }
    document.addEventListener('focusin', function (e) { remember(e.target); }, true);
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('button, a, [tabindex]') : null;
      remember(t);
    }, true);

    function restoreIfLost() {
      var ae = document.activeElement;
      var lost = !ae || ae === document.body || drawer.contains(ae);
      if (lost && lastOutside && document.contains(lastOutside) && !drawer.classList.contains('drawer--is-open')) {
        try { lastOutside.focus({ preventScroll: true }); } catch (err) { try { lastOutside.focus(); } catch (e2) {} }
      }
    }
    /* --- 6) Guarantee keyboard focus is inside the drawer while it is open (WCAG 2.4.3).
       The theme focuses the drawer container when it opens, but it also re-renders the cart
       contents asynchronously right after "Add to cart"; if focus is on a control that gets
       replaced, or the container focus did not take, Tab would walk the page behind the drawer.
       We move focus to the drawer container whenever the drawer is open and focus is not inside it. --- */
    var lastInDrawer = null; /* descriptor of the last control focused inside the drawer */
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || t === drawer || !drawer.contains(t)) return;
      lastInDrawer = { tag: t.tagName, label: t.getAttribute('aria-label'), name: t.getAttribute('name'), cls: t.className && String(t.className).split(/\s+/)[0] };
    }, true);
    function findInDrawer(d) {
      if (!d) return null;
      var cands = drawer.querySelectorAll(d.tag);
      for (var i = 0; i < cands.length; i++) {
        var c = cands[i];
        if (c.offsetParent === null) continue;
        if (d.label && c.getAttribute('aria-label') === d.label) return c;
        if (d.name && c.getAttribute('name') === d.name) return c;
        if (!d.label && !d.name && d.cls && c.className && String(c.className).split(/\s+/)[0] === d.cls) return c;
      }
      return null;
    }
    function focusIntoDrawer() {
      if (!drawer.classList.contains('drawer--is-open')) return;
      var ae = document.activeElement;
      if (ae && ae !== document.body && drawer.contains(ae)) return;
      var target = findInDrawer(lastInDrawer) || drawer;
      if (target === drawer && !drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (err) { try { target.focus(); } catch (e2) {} }
    }

    if (window.MutationObserver) {
      var wasOpen = drawer.classList.contains('drawer--is-open');
      new MutationObserver(function () {
        var open = drawer.classList.contains('drawer--is-open');
        if (!wasOpen && open) {
          lastInDrawer = null; /* a fresh open starts at the drawer container, not at last session's control */
          window.setTimeout(focusIntoDrawer, 150);
          window.setTimeout(focusIntoDrawer, 800); /* after the async cart re-render */
        }
        if (wasOpen && !open) {
          window.setTimeout(restoreIfLost, 60);
          window.setTimeout(restoreIfLost, 600); /* after the slide-out transition hides the drawer */
        }
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

      /* If the cart contents are re-rendered while the drawer is open and focus was lost, put it back */
      new MutationObserver(function () {
        if (drawer.classList.contains('drawer--is-open')) window.setTimeout(focusIntoDrawer, 30);
      }).observe(drawer, { childList: true, subtree: true });
    }

    /* --- 7) Tab key inside the drawer (WCAG 2.1.1 / 2.1.2). The theme's own focus trap computes the
       first and last focusable controls at the moment the drawer opens. When the first item is added to
       an EMPTY cart, the cart contents are still hidden at that moment, so the Close button is both
       "first" and "last"; the theme's keydown handler then cancels every Tab press on Close and re-focuses
       Close, pinning keyboard focus there (reproduced Sept 9, 2026). Its namespaced handlers also cannot be
       removed once registered. We stop the Tab keydown from reaching that handler and do the wrap-around
       ourselves, from a fresh list of the drawer's controls on every key press. --- */
    function drawerFocusables() {
      var els = drawer.querySelectorAll('button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex^="-"])');
      return Array.prototype.filter.call(els, function (el) {
        return el.offsetParent !== null && !el.disabled && el.getAttribute('aria-hidden') !== 'true' && el !== drawer;
      });
    }
    var tabAt = 0, tabShift = false;
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      if (!drawer.classList.contains('drawer--is-open')) return;
      e.stopPropagation(); /* keeps the theme's stale trap handler from cancelling the key */
      tabAt = Date.now(); tabShift = !!e.shiftKey;
      var list = drawerFocusables();
      if (!list.length) return;
      var ae = document.activeElement;
      if (!ae || ae === document.body || !drawer.contains(ae) || ae === drawer) {
        e.preventDefault();
        (tabShift ? list[list.length - 1] : list[0]).focus();
        return;
      }
      if (!tabShift && ae === list[list.length - 1] && ae.tagName !== 'IFRAME') { e.preventDefault(); list[0].focus(); return; }
      if (tabShift && ae === list[0]) { e.preventDefault(); list[list.length - 1].focus(); return; }
      /* otherwise the browser's normal sequential focus moves to the next control in the drawer */
    }, true);
    /* If a keyboard Tab carried focus out of the open drawer (e.g. past an embedded payment button), bring it back */
    document.addEventListener('focusin', function (e) {
      if (!drawer.classList.contains('drawer--is-open')) return;
      if (Date.now() - tabAt > 300) return; /* only keyboard-driven focus moves, never clicks or app pop-ups */
      var t = e.target;
      if (!t || drawer.contains(t)) return;
      var list = drawerFocusables();
      if (list.length) (tabShift ? list[list.length - 1] : list[0]).focus();
    }, true);
  }

  /* ---------- Round 2 additions (2026-09-09) ---------- */

  /* Shared polite status region for non-cart announcements */
  var status = document.getElementById('a11y-status');
  if (!status) {
    status = document.createElement('div');
    status.id = 'a11y-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    document.body.appendChild(status);
  }
  function say(msg) {
    status.textContent = '';
    window.setTimeout(function () { status.textContent = msg; }, 50);
  }

  /* --- 8) Header drop-down menus (WCAG 4.1.2). The theme builds each top-level menu item with a
     drop-down as <details><summary>; it opens the menu on hover and on keyboard focus, but it records the
     expanded state on the <details> element, while the <summary> the user is focused on always says
     aria-expanded="false". Keep the summary's state in sync with the menu, and let Escape close an open
     menu and return focus to its control. --- */
  var navDetails = document.querySelectorAll('details.site-nav__details');
  Array.prototype.forEach.call(navDetails, function (det) {
    var summary = det.querySelector('summary');
    if (!summary) return;
    function sync() { summary.setAttribute('aria-expanded', det.hasAttribute('open') ? 'true' : 'false'); }
    sync();
    if (window.MutationObserver) new MutationObserver(sync).observe(det, { attributes: true, attributeFilter: ['open'] });
    det.addEventListener('toggle', sync);
    det.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!det.hasAttribute('open')) return;
      e.stopPropagation();
      try { summary.focus(); } catch (err) {}          /* focus first, so the theme's focus-in handler does not reopen it */
      det.removeAttribute('open');
      det.setAttribute('aria-expanded', 'false');
      sync();
    });
  });

  /* --- 9) Search suggestions (WCAG 4.1.3 Status Messages). The theme renders suggestions into
     #predictive-search as the user types, but nothing tells a screen-reader user that they appeared,
     and the search box's aria-expanded never changes. Announce a short summary of what appeared and
     keep the combobox state current. --- */
  Array.prototype.forEach.call(document.querySelectorAll('predictive-search'), function (ps) {
    var input = ps.querySelector('input[type="search"]');
    var results = ps.querySelector('#predictive-search');
    if (!input || !results) return;
    var lastMsg = null, timer = null;
    function isOpen() { return results.style.display !== 'none' && results.style.display !== '' && results.querySelector('#predictive-search-results'); }
    function describe() {
      var groups = results.querySelectorAll('.results');
      var parts = [];
      Array.prototype.forEach.call(groups, function (g) {
        var n = g.querySelectorAll('li[role="option"], li').length;
        if (!n) return;
        var h = g.querySelector('h1, h2, h3, h4, h5, h6');
        var label = h ? h.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : 'results';
        if (n === 1 && /s$/.test(label)) label = label.replace(/s$/, '');
        parts.push(n + ' ' + label);
      });
      if (!parts.length) return 'No search suggestions.';
      return 'Search suggestions: ' + parts.join(', ') + '. Press Tab to review them.';
    }
    function update() {
      var open = !!isOpen();
      input.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) { lastMsg = null; return; }
      var msg = describe();
      if (msg !== lastMsg) { lastMsg = msg; say(msg); }
    }
    function schedule() { window.clearTimeout(timer); timer = window.setTimeout(update, 200); }
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(results, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
  });

  /* --- 10) Pop-up dialogs (WCAG 4.1.2, 2.4.3). The theme's modals (newsletter sign-up, quick shop,
     video) carry no dialog role or name, and when one closes without having been opened from a button,
     focus is dropped on the page body. Give each modal dialog semantics when it opens and return focus
     to the element that had it before the dialog appeared. --- */
  Array.prototype.forEach.call(document.querySelectorAll('.modal'), function (modal) {
    var before = null;
    function label(final) {
      if (modal.getAttribute('aria-labelledby') || modal.getAttribute('aria-label')) return;
      var h = modal.querySelector('h1, h2, h3, .h1, .h2, .h3, .product-single__title');
      if (h && h.textContent.replace(/\s+/g, ' ').trim()) {
        if (!h.id) h.id = (modal.id || 'modal') + '-title';
        modal.setAttribute('aria-labelledby', h.id);
      } else if (final) {
        var name = /quick-shop/.test(modal.className) ? 'Quick shop' : (/video/i.test(modal.id) ? 'Video' : 'Dialog');
        modal.setAttribute('aria-label', name);
      }
    }
    function onOpen() {
      if (!modal.getAttribute('role')) modal.setAttribute('role', 'dialog');
      if (!modal.getAttribute('aria-modal')) modal.setAttribute('aria-modal', 'true');
      var ae = document.activeElement;
      before = (ae && ae !== document.body && !modal.contains(ae)) ? ae : null;
      label(false);
      window.setTimeout(function () { label(false); }, 400);  /* quick-shop content is loaded after the modal opens */
      window.setTimeout(function () { label(true); }, 1500);
    }
    function onClose() {
      window.setTimeout(function () {
        var ae = document.activeElement;
        if ((!ae || ae === document.body) && before && document.contains(before)) {
          try { before.focus({ preventScroll: true }); } catch (err) { try { before.focus(); } catch (e2) {} }
        }
        before = null;
      }, 600); /* after the theme's own 500 ms close routine, which restores focus only for button-opened modals */
    }
    if (window.MutationObserver) {
      var active = modal.classList.contains('modal--is-active');
      new MutationObserver(function () {
        var now = modal.classList.contains('modal--is-active');
        if (!active && now) onOpen();
        if (active && !now) onClose();
        active = now;
      }).observe(modal, { attributes: true, attributeFilter: ['class'] });
    }
  });

  /* --- 11) Pagination (WCAG 1.3.1, 2.4.4). Mark the pagination block as a navigation landmark, flag the
     current page, and give the bare page-number links a name that says what they are. --- */
  Array.prototype.forEach.call(document.querySelectorAll('.pagination'), function (pag) {
    if (pag.tagName !== 'NAV' && !pag.getAttribute('role')) {
      pag.setAttribute('role', 'navigation');
      if (!pag.getAttribute('aria-label')) pag.setAttribute('aria-label', 'Pagination');
    }
    var cur = pag.querySelector('.current');
    if (cur && !cur.getAttribute('aria-current')) cur.setAttribute('aria-current', 'page');
    Array.prototype.forEach.call(pag.querySelectorAll('a'), function (a) {
      var t = a.textContent.replace(/\s+/g, ' ').trim();
      if (/^\d+$/.test(t) && !a.getAttribute('aria-label')) a.setAttribute('aria-label', 'Page ' + t);
    });
  });
})();
